// src/private/src/pages/app/leads/LeadDetailPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '@src/api/clients';
import { useMessage } from '@src/context';
import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';

// Mirrors the backend's canonical delivery buckets (apps.utils.lead_delivery)
// so the chip here always matches the /leads Delivery filter.
const DELIVERY_META = {
  delivered: {
    label: 'Delivered',
    color: 'success',
    hint: 'Sent to the client webhook and confirmed by their CRM.',
  },
  declined: {
    label: 'Declined by client',
    color: 'warning',
    hint: 'The client’s CRM received the lead but declined it on a business rule (duplicate, out of area, suppressed…). Not a delivery failure.',
  },
  rejected_data: {
    label: 'Rejected · bad data',
    color: 'warning',
    hint: 'The client’s endpoint answered with a 4xx rejecting this lead’s payload (bad email format, missing ZIP…). The endpoint is up — this is a per-lead data problem, not a delivery outage. Not retried.',
  },
  rejected: {
    label: 'Rejected (unconfirmed)',
    color: 'error',
    hint: 'The webhook POST got a 2xx but the CRM never confirmed the lead — likely an integration break worth a look.',
  },
  dead: {
    label: 'Dead-lettered',
    color: 'error',
    hint: 'Delivery gave up after repeated failed webhook attempts. The client never received this lead.',
  },
  undelivered: {
    label: 'Undelivered',
    color: 'default',
    hint: 'No webhook sent yet — queued, still retrying, or no webhook is configured.',
  },
  skipped: {
    label: 'Skipped',
    color: 'default',
    hint: 'Our own rules chose not to send this lead (7-day duplicate, out-of-area skip, renter routing, or webhook disabled).',
  },
};

const DECLINE_REASON_LABELS = {
  duplicate: 'Duplicate',
  out_of_area: 'Out of area',
  suppressed: 'Suppressed',
  litigation: 'Litigation',
  trestle: 'Trestle',
  other: 'Client rule',
};

const deliveryChipLabel = (lead) => {
  const meta = DELIVERY_META[lead.delivery_status];
  if (!meta) return null;
  if (lead.delivery_status === 'declined' && DECLINE_REASON_LABELS[lead.decline_reason]) {
    return `${meta.label} · ${DECLINE_REASON_LABELS[lead.decline_reason]}`;
  }
  return meta.label;
};

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-US', { timeZone: 'America/New_York' })
    : null;

const statusCodeColor = (code) => {
  if (code == null) return 'text.secondary';
  if (code >= 200 && code < 300) return 'success.main';
  if (code >= 400 && code < 500) return 'warning.main';
  return 'error.main';
};

function CopyButton({ value, onCopied }) {
  if (!value) return null;
  return (
    <Tooltip title="Copy">
      <IconButton
        size="small"
        sx={{ ml: 0.5, opacity: 0.6, '&:hover': { opacity: 1 } }}
        onClick={() => {
          navigator.clipboard?.writeText(String(value));
          onCopied?.();
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

function FieldValue({ value, type, onCopied, copyable }) {
  if (value === true || value === false) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color={value ? 'success' : 'default'}
        label={value ? 'Yes' : 'No'}
      />
    );
  }
  const text = type === 'datetime' ? formatDateTime(value) : value;
  if (text === null || text === undefined || text === '') {
    return (
      <Typography variant="body2" color="text.disabled">
        &mdash;
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        wordBreak: 'break-word',
        ...(type === 'mono' ? { fontFamily: 'monospace', fontSize: 12 } : {}),
      }}
    >
      {String(text)}
      {copyable ? <CopyButton value={text} onCopied={onCopied} /> : null}
    </Typography>
  );
}

// Label/value rows in a tight two-column grid — half the vertical space of the
// old stacked layout.
function FieldGrid({ fields, data, onCopied }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, 34%) 1fr',
        columnGap: 1.5,
        rowGap: 1,
        alignItems: 'baseline',
      }}
    >
      {fields.map(({ label, name, type, copyable }) => (
        <React.Fragment key={name}>
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
            {label}
          </Typography>
          <FieldValue
            value={data[name]}
            type={type}
            copyable={copyable}
            onCopied={onCopied}
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

// Editable section card. Kept at module scope (not re-created per render) so
// edit-mode text fields keep focus while typing.
function SectionCard({ title, fields, data, canEdit, onSave, onCopied }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const editableFields = fields.filter((field) => field.editable !== false);

  const startEdit = () => {
    const initial = {};
    editableFields.forEach(({ name }) => {
      const value = data[name];
      initial[name] = value === null || value === undefined ? '' : String(value);
    });
    setForm(initial);
    setError('');
    setEditing(true);
  };

  const handleSave = async () => {
    const cleaned = {};
    editableFields.forEach(({ name }) => {
      const raw = form[name];
      if (raw === '') {
        cleaned[name] = null;
      } else if (raw?.toLowerCase?.() === 'true') {
        cleaned[name] = true;
      } else if (raw?.toLowerCase?.() === 'false') {
        cleaned[name] = false;
      } else {
        cleaned[name] = raw;
      }
    });
    setSaving(true);
    setError('');
    try {
      await onSave(cleaned);
      setEditing(false);
    } catch (err) {
      console.error('Failed to save lead section:', err);
      setError(
        err?.response?.data?.detail || 'Failed to save changes. Please retry.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        height: '100%',
        ...(editing
          ? {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
            }
          : {}),
      })}
    >
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        sx={{ py: 1.5 }}
        action={
          !canEdit || editableFields.length === 0 ? null : editing ? (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Save">
                <span>
                  <IconButton size="small" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SaveIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={() => setEditing(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Tooltip title="Edit section">
              <IconButton size="small" onClick={startEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <Divider />
      <CardContent sx={{ pt: 2 }}>
        {editing ? (
          <Stack spacing={1.5}>
            {fields.map(({ label, name, editable }) =>
              editable === false ? null : (
                <TextField
                  key={name}
                  label={label}
                  value={form[name] ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, [name]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              )
            )}
            {error ? (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <FieldGrid fields={fields} data={data} onCopied={onCopied} />
        )}
      </CardContent>
    </Card>
  );
}

function DeliveryCard({ lead, isStaff, onResent, onCopied }) {
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const meta = DELIVERY_META[lead.delivery_status] || null;

  const handleResend = async () => {
    const confirmed = window.confirm(
      'Re-push this lead to the client webhook? This clears the "Webhook Sent" ' +
        'stamp so the lead is re-delivered within a few minutes.'
    );
    if (!confirmed) return;
    setResending(true);
    setError('');
    try {
      await api.request({
        url: 'dashboard/leads/reset-webhook/',
        method: 'POST',
        payload: { selected_ids: [lead.id], filters: {} },
      });
      onResent?.();
    } catch (err) {
      console.error('Failed to queue lead re-delivery:', err);
      setError(err?.response?.data?.detail || 'Failed to queue re-delivery.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title="Delivery"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        sx={{ py: 1.5 }}
        action={
          isStaff ? (
            <Tooltip title="Clear the Webhook Sent stamp so this lead re-delivers to the client webhook">
              <span>
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  startIcon={
                    resending ? <CircularProgress size={14} /> : <ReplayIcon />
                  }
                  onClick={handleResend}
                  disabled={resending}
                >
                  Resend
                </Button>
              </span>
            </Tooltip>
          ) : null
        }
      />
      <Divider />
      <CardContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          {meta ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color={meta.color} label={deliveryChipLabel(lead)} />
              <Typography variant="caption" color="text.secondary">
                {meta.hint}
              </Typography>
            </Stack>
          ) : null}
          <FieldGrid
            onCopied={onCopied}
            data={lead}
            fields={[
              { label: 'Webhook Sent (EST)', name: 'webhook_sent_datetime', type: 'datetime' },
              { label: 'Response Message', name: 'response_message' },
              { label: 'Failed Attempts', name: 'failed_webhook_attempts' },
              { label: 'Success Post', name: 'success_post' },
              { label: 'Processed', name: 'processed' },
            ]}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(110px, 34%) 1fr',
              columnGap: 1.5,
              alignItems: 'baseline',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              HTTP Status
            </Typography>
            {lead.last_webhook_status_code != null ? (
              <Typography
                variant="body2"
                sx={{
                  color: statusCodeColor(lead.last_webhook_status_code),
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {lead.last_webhook_status_code}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">
                &mdash;
              </Typography>
            )}
          </Box>
          {lead.post_response ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Post Response
              </Typography>
              <Box
                sx={(theme) => ({
                  mt: 0.5,
                  p: 1.25,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor:
                    theme.palette.background.level1 ??
                    theme.palette.action.hover,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 180,
                  overflow: 'auto',
                })}
              >
                {String(lead.post_response)}
              </Box>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function BackendRecordsCard({ records }) {
  if (!Array.isArray(records) || records.length === 0) return null;
  return (
    <Card variant="outlined">
      <CardHeader
        title={`Backend Data (${records.length} record${records.length === 1 ? '' : 's'})`}
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        sx={{ py: 1.5 }}
      />
      <Divider />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Appointment</TableCell>
              <TableCell>Issue</TableCell>
              <TableCell>Demo</TableCell>
              <TableCell>Sold Date</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => (
              <TableRow key={record.unique_id || index} hover>
                <TableCell>{record.created_date || '—'}</TableCell>
                <TableCell>{record.appointment_date || '—'}</TableCell>
                <TableCell>{record.issue ? '✓' : '—'}</TableCell>
                <TableCell>{record.demo ? '✓' : '—'}</TableCell>
                <TableCell>{record.sold_date || '—'}</TableCell>
                <TableCell align="right">
                  {record.gross_amount != null && record.gross_amount !== ''
                    ? `$${record.gross_amount}`
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  {record.net_amount != null && record.net_amount !== ''
                    ? `$${record.net_amount}`
                    : '—'}
                </TableCell>
                <TableCell>{record.status || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function TrackingCard({ lead, onCopied }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card variant="outlined">
      <CardHeader
        title="Tracking & Attribution IDs"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        sx={{ py: 1.5, cursor: 'pointer' }}
        onClick={() => setExpanded((prev) => !prev)}
        action={
          <IconButton
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        }
      />
      {expanded ? (
        <>
          <Divider />
          <CardContent sx={{ pt: 2 }}>
            <FieldGrid
              onCopied={onCopied}
              data={lead}
              fields={[
                { label: 'IP Address', name: 'ip_address', type: 'mono', copyable: true },
                { label: 'User Agent', name: 'user_agent', type: 'mono' },
                { label: 'FBC', name: 'fbc', type: 'mono', copyable: true },
                { label: 'FBP', name: 'fbp', type: 'mono', copyable: true },
                { label: 'Trusted Form', name: 'trusted_form', type: 'mono', copyable: true },
                { label: 'Lead FBTrace ID', name: 'fb_lead_fbtrace_id', type: 'mono', copyable: true },
                { label: 'CompleteReg FBTrace ID', name: 'fb_completeregistration_fbtrace_id', type: 'mono', copyable: true },
              ]}
            />
          </CardContent>
        </>
      ) : null}
    </Card>
  );
}

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const message = useMessage();
  const user = useEffectiveUser();
  const memberships = Array.isArray(user?.client_memberships)
    ? user.client_memberships
    : [];
  const isClientMember =
    !user?.is_staff && !user?.is_superuser && memberships.length > 0;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const backPath =
    typeof location.state?.from === 'string' && location.state.from.startsWith('/')
      ? location.state.from
      : '/leads';

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchLead = useCallback(() => {
    return api
      .request({ url: `dashboard/leads/${leadId}/`, method: 'GET' })
      .then((res) => {
        setLead(res.data);
        setLoadError('');
      })
      .catch((err) => {
        console.error('Error fetching lead:', err);
        setLoadError('Failed to load this lead.');
      });
  }, [leadId]);

  useEffect(() => {
    setLoading(true);
    fetchLead().finally(() => setLoading(false));
  }, [fetchLead]);

  // Section saves merge into the full payload the API expects on PUT, then
  // re-fetch so computed fields (delivery_status) stay honest.
  const saveSection = useCallback(
    async (changes) => {
      await api.request({
        url: `dashboard/leads/${leadId}/`,
        method: 'PUT',
        payload: { ...lead, ...changes },
      });
      setLead((prev) => ({ ...prev, ...changes }));
      message.success('Changes saved.');
      fetchLead();
    },
    [lead, leadId, fetchLead, message]
  );

  const notifyCopied = useCallback(
    () => message.success('Copied to clipboard.'),
    [message]
  );

  const fullName = useMemo(() => {
    const name = [lead?.first_name, lead?.last_name].filter(Boolean).join(' ');
    return name || 'Lead';
  }, [lead]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !lead) {
    return (
      <Box p={3}>
        <Button onClick={() => navigate(backPath)} startIcon={<ArrowBackIcon />}>
          Back to Leads
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {loadError || 'Lead not found.'}
        </Alert>
      </Box>
    );
  }

  const deliveryMeta = DELIVERY_META[lead.delivery_status];
  const canEdit = !isClientMember;

  return (
    <Box p={3}>
      <Button
        onClick={() => navigate(backPath)}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        Back to Leads
      </Button>

      {/* Header: who is this lead, whose is it, how did delivery go. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="h4" fontWeight={700}>
              {fullName}
            </Typography>
            {deliveryMeta ? (
              <Tooltip title={deliveryMeta.hint}>
                <Chip color={deliveryMeta.color} label={deliveryChipLabel(lead)} />
              </Tooltip>
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[
              lead.client_name,
              lead.vertical,
              lead.date_created
                ? `Created ${formatDateTime(lead.date_created)} EST`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6} lg={4}>
          <SectionCard
            title="Contact"
            canEdit={canEdit}
            onSave={saveSection}
            onCopied={notifyCopied}
            data={lead}
            fields={[
              { label: 'First Name', name: 'first_name' },
              { label: 'Last Name', name: 'last_name' },
              { label: 'Email', name: 'email', copyable: true },
              { label: 'Phone', name: 'phone', copyable: true },
              { label: 'Homeowner', name: 'homeowner' },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SectionCard
            title="Location"
            canEdit={canEdit}
            onSave={saveSection}
            onCopied={notifyCopied}
            data={lead}
            fields={[
              { label: 'Address', name: 'address' },
              { label: 'City', name: 'city' },
              { label: 'State', name: 'state' },
              { label: 'ZIP Code', name: 'zip_code' },
              { label: 'Territory', name: 'territory' },
            ]}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <DeliveryCard
            lead={lead}
            isStaff={isStaff}
            onCopied={notifyCopied}
            onResent={() => {
              message.success(
                'Queued for re-delivery — the webhook task re-pushes within a few minutes.'
              );
              fetchLead();
            }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SectionCard
            title="Source & Campaign"
            canEdit={canEdit}
            onSave={saveSection}
            onCopied={notifyCopied}
            data={lead}
            fields={[
              { label: 'Source', name: 'source' },
              { label: 'Medium', name: 'medium' },
              { label: 'Campaign', name: 'campaign_name' },
              { label: 'Ad Set', name: 'ad_set_name' },
              { label: 'Ad Name', name: 'ad_name' },
              { label: 'Vertical', name: 'vertical' },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SectionCard
            title="Self-Book"
            canEdit={canEdit}
            onSave={saveSection}
            onCopied={notifyCopied}
            data={lead}
            fields={[
              { label: 'Appointment', name: 'self_book_appointment_date_time' },
              { label: 'Spouse', name: 'self_book_spouse' },
              { label: 'Notes', name: 'self_book_notes' },
              { label: 'Booked OK', name: 'success_self_book', editable: false },
            ]}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <TrackingCard lead={lead} onCopied={notifyCopied} />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SectionCard
            title="Qualification"
            canEdit={canEdit}
            onSave={saveSection}
            onCopied={notifyCopied}
            data={lead}
            fields={[
              { label: 'Windows Reason', name: 'windows_reason' },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <BackendRecordsCard records={lead.related_backend_data} />
        </Grid>
      </Grid>
    </Box>
  );
}
